import { formatCurrency } from '../../../../lib/formatters';
import type { StatementTransaction } from '../useAccountStatement';

interface StatementTableProps {
  transactions: StatementTransaction[];
  currency?: string;
  loading?: boolean;
}

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  pago: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Pago' },
  recebido: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Recebido' },
  pendente: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pendente' },
  vencido: { bg: 'bg-red-100', text: 'text-red-700', label: 'Vencido' },
  atrasado: { bg: 'bg-red-100', text: 'text-red-700', label: 'Atrasado' },
};

const RECURRENCE_ICONS: Record<string, string> = {
  monthly: 'ri-repeat-line',
  weekly: 'ri-repeat-line',
  daily: 'ri-repeat-line',
  yearly: 'ri-repeat-line',
};

const formatDate = (date: string) => {
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateShort = (date: string) => {
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

function SkeletonRow() {
  return (
    <tr>
      <td className="px-4 py-3"><div className="w-16 h-4 bg-gray-100 rounded animate-pulse" /></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-100 rounded-lg animate-pulse flex-shrink-0" />
          <div>
            <div className="w-32 h-4 bg-gray-100 rounded animate-pulse mb-1" />
            <div className="w-20 h-3 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </td>
      <td className="px-4 py-3"><div className="w-16 h-5 bg-gray-100 rounded-full animate-pulse" /></td>
      <td className="px-4 py-3 text-right"><div className="w-20 h-5 bg-gray-100 rounded animate-pulse ml-auto" /></td>
      <td className="px-4 py-3 text-right"><div className="w-24 h-5 bg-gray-100 rounded animate-pulse ml-auto" /></td>
    </tr>
  );
}

export function StatementTable({ transactions, currency = 'BRL', loading = false }: StatementTableProps) {
  // Group by date for display
  const grouped = transactions.reduce<Record<string, StatementTransaction[]>>((acc, tx) => {
    const key = tx.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tx);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 w-28">Data</th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500">Descrição</th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 w-28">Status</th>
              <th className="px-4 py-3 text-right text-xs font-600 text-gray-500 w-32">Valor</th>
              <th className="px-4 py-3 text-right text-xs font-600 text-gray-500 w-36">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {[1,2,3,4,5,6,7,8].map(i => <SkeletonRow key={i} />)}
          </tbody>
        </table>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-16 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-file-list-3-line text-3xl text-gray-300" />
        </div>
        <p className="text-sm font-700 text-gray-500 mb-1">Nenhum lançamento encontrado</p>
        <p className="text-xs text-gray-400 max-w-xs">Ajuste os filtros ou o período para ver os lançamentos desta conta.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="sticky top-0 z-[1]">
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 w-28">Data</th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500">Descrição</th>
              <th className="px-4 py-3 text-left text-xs font-600 text-gray-500 w-28">Status</th>
              <th className="px-4 py-3 text-right text-xs font-600 text-gray-500 w-32">Valor</th>
              <th className="px-4 py-3 text-right text-xs font-600 text-gray-500 w-36">
                <span className="flex items-center justify-end gap-1">
                  Saldo após
                  <i className="ri-information-line text-gray-400" title="Saldo acumulado após lançamentos pagos/recebidos" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDates.map((date) => {
              const txs = grouped[date];
              return (
                <>
                  {/* Date separator */}
                  <tr key={`sep-${date}`} className="bg-gray-50/60">
                    <td colSpan={5} className="px-4 py-2">
                      <span className="text-xs font-700 text-gray-400 uppercase tracking-wide">
                        {formatDate(date)}
                      </span>
                    </td>
                  </tr>
                  {txs.map((tx, idx) => {
                    const isTransfer = tx.transaction_subtype === 'transferencia';
                    const isIncome = tx.type === 'receita';
                    const isPaid = tx.affectsBalance;

                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30';
                    const iconBg = isTransfer
                      ? 'bg-teal-50'
                      : isIncome ? 'bg-emerald-50' : 'bg-red-50';
                    const iconColor = isTransfer
                      ? 'ri-arrow-left-right-line text-teal-500'
                      : isIncome
                        ? 'ri-arrow-down-line text-emerald-500'
                        : 'ri-arrow-up-line text-red-500';
                    const valueColor = isTransfer
                      ? 'text-teal-600'
                      : isIncome ? 'text-emerald-600' : 'text-red-500';
                    const sign = isTransfer ? '⇄ ' : isIncome ? '+' : '−';
                    const status = STATUS_STYLE[tx.status] ?? { bg: 'bg-gray-100', text: 'text-gray-500', label: tx.status };
                    const hasInstallments = tx.installments > 1;
                    const hasRecurrence = tx.recurrence && tx.recurrence !== 'none' && tx.recurrence !== 'once';

                    return (
                      <tr
                        key={tx.id}
                        className={`${rowBg} hover:bg-emerald-50/20 transition-colors border-b border-gray-50 group`}
                      >
                        {/* Date */}
                        <td className="px-4 py-3">
                          {tx.paid_at && tx.paid_at !== tx.date && isPaid ? (
                            <div title={`Vencimento: ${formatDate(tx.date)}`}>
                              <span className="text-xs text-emerald-600 font-600 flex items-center gap-0.5">
                                <i className="ri-checkbox-circle-line text-[10px]" />
                                {formatDateShort(tx.paid_at)}
                              </span>
                              <span className="text-[10px] text-gray-400 block mt-0.5 line-through">
                                venc. {formatDateShort(tx.date)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 font-500">{formatDateShort(tx.date)}</span>
                          )}
                        </td>

                        {/* Description */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${iconBg}`}>
                              <i className={`${iconColor} text-sm`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className={`text-sm font-600 text-gray-800 truncate max-w-[220px] ${!isPaid ? 'opacity-70' : ''}`}>
                                  {tx.description}
                                </p>
                                {isTransfer && (
                                  <span className="text-[10px] bg-teal-50 text-teal-600 border border-teal-100 px-1.5 py-0.5 rounded-full font-600 whitespace-nowrap shrink-0">
                                    Transf.
                                  </span>
                                )}
                                {hasInstallments && (
                                  <span className="text-[10px] bg-violet-50 text-violet-600 border border-violet-100 px-1.5 py-0.5 rounded-full font-600 whitespace-nowrap shrink-0">
                                    {tx.installment_number}/{tx.installments}x
                                  </span>
                                )}
                                {hasRecurrence && !hasInstallments && (
                                  <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded-full font-600 whitespace-nowrap shrink-0 flex items-center gap-0.5">
                                    <i className={`${RECURRENCE_ICONS[tx.recurrence ?? ''] ?? 'ri-repeat-line'} text-[10px]`} />
                                    Recorrente
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {tx.category}
                                {tx.payment_method_detail && (
                                  <span className="ml-1.5 text-emerald-600 font-500">· via {({'pix':'Pix','boleto':'Boleto','ted':'TED','doc':'DOC','debito':'Débito','dinheiro':'Dinheiro','saque':'Saque','outro':'Outro'} as Record<string,string>)[tx.payment_method_detail] ?? tx.payment_method_detail}</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-500 ${status.bg} ${status.text}`}>
                            {status.label}
                          </span>
                        </td>

                        {/* Value */}
                        <td className="px-4 py-3 text-right">
                          <span className={`text-sm font-700 ${valueColor} ${!isPaid ? 'opacity-60' : ''}`}>
                            {sign}{formatCurrency(tx.amount, currency)}
                          </span>
                        </td>

                        {/* Running balance */}
                        <td className="px-4 py-3 text-right">
                          {tx.runningBalance !== null ? (
                            <span className={`text-sm font-700 ${tx.runningBalance >= 0 ? 'text-gray-800' : 'text-red-600'}`}>
                              {formatCurrency(tx.runningBalance, currency)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300 font-500" title="Não afeta o saldo (pendente)">
                              — — —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
